import { useState } from "react";
import { usePermission } from "@/auth/usePermission";
import { useNavigate } from "react-router-dom";
import {
  useV1InventoryUomList,
  useV1InventoryUomCreate,
  useV1InventoryUomPartialUpdate,
  v1InventoryUomList,
} from "@/api/generated/v1/v1";
import { DataTableToolbar } from "@/shared/components/DataTableToolbar";
import { exportToExcel, exportToPDF } from "@/shared/utils/export";
import { PageBanner } from "@/shared/components/PageBanner";

export function UomListPage() {
  const canManage = usePermission("inventory.manage");
  const navigate = useNavigate();

  const [showForm, setShowForm] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);

  const { data, isLoading, refetch } = useV1InventoryUomList(
    { search: searchTerm || undefined, ...activeFilters } as any,
    { query: {} }
  );

  const handleFilterChange = (name: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [name]: value }));
  };

  const fetchExportData = async () => {
    try {
      setIsExporting(true);
      const res = await v1InventoryUomList({
        export: "true",
        ...(searchTerm && { search: searchTerm }),
        ...activeFilters
      } as any);
      if ((res as any).truncated) {
        alert("تعداد رکوردها بیش از ۱۰,۰۰۰ مورد است و اکسپورت محدود شد.");
      }
      return (res as any).results || res;
    } catch (err) {
      alert("خطا در دریافت اطلاعات برای خروجی");
      throw err;
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    { header: "کد", accessor: (row: any) => row.code },
    { header: "نام", accessor: (row: any) => row.name },
    { header: "نماد", accessor: (row: any) => row.symbol || "—" },
    { header: "وضعیت", accessor: (row: any) => row.is_active ? "فعال" : "غیرفعال" },
  ];

  const handleExportExcel = async () => {
    const rawData = await fetchExportData();
    await exportToExcel(rawData, columns, "uom_export");
  };

  const handleExportPDF = async () => {
    const rawData = await fetchExportData();
    await exportToPDF(rawData, columns, "uom_export");
  };

  const { mutate: create, isPending: isCreating } = useV1InventoryUomCreate({
    mutation: {
      onSuccess: () => {
        setShowForm(false);
        setCode(""); setName(""); setSymbol(""); setError(null);
        refetch();
      },
      onError: (err: any) => {
        setError(err?.response?.data?.detail || err?.response?.data?.code?.[0] || "خطا در ایجاد واحد");
      },
    },
  });

  const { mutate: deactivate } = useV1InventoryUomPartialUpdate({
    mutation: { onSuccess: () => refetch() },
  });

  const handleSubmit = () => {
    setError(null);
    if (!code.trim()) { setError("کد الزامی است"); return; }
    if (!name.trim()) { setError("نام الزامی است"); return; }
    create({ data: { code: code.trim().toUpperCase(), name: name.trim(), symbol: symbol.trim(), is_active: true } });
  };

  const inputStyle = {
    width: "100%", padding: "0.6rem 0.9rem",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid var(--glass-border)",
    borderRadius: "8px", color: "var(--color-text)",
    boxSizing: "border-box" as const,
  };

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="واحدهای اندازه‌گیری (UoM)"
        subtitle="تعریف و مدیریت واحدهای پایه شمارش، وزن و طول"
        icon="📐"
        helpLink="/help/inventory/uom-list"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate("/inventory")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← داشبورد انبار
          </button>
          {canManage && !showForm && (
            <button
              onClick={() => setShowForm(true)}
              style={{
                padding: "0.6rem 1.25rem",
                borderRadius: "12px",
                background: "#ffffff",
                color: "#007270",
                fontWeight: 700,
                fontSize: "0.9rem",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>➕</span>
              <span>ایجاد واحد جدید</span>
            </button>
          )}
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>

        <DataTableToolbar
          onSearch={setSearchTerm}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onExportExcel={handleExportExcel}
          onExportPDF={handleExportPDF}
          isExporting={isExporting}
          filters={[
            {
              name: "is_active",
              label: "وضعیت",
              options: [
                { label: "فعال", value: "True" },
                { label: "غیرفعال", value: "False" },
              ],
            },
          ]}
        />

        {/* فرم */}
        {showForm && (
          <div className="mb-6 p-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid var(--glass-border)", borderRadius: "10px" }}>
            <h2 className="font-bold mb-4">واحد جدید</h2>
            {error && (
              <div className="mb-3 p-3" style={{ background: "rgba(255,0,0,0.1)", borderLeft: "4px solid red", borderRadius: "8px", color: "red", fontSize: "0.9rem" }}>
                {error}
              </div>
            )}
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" }}>
              <div style={{ flex: 1, minWidth: "100px" }}>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>کد *</label>
                <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="kg" dir="ltr" style={inputStyle} />
              </div>
              <div style={{ flex: 2, minWidth: "150px" }}>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>نام *</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="کیلوگرم" style={inputStyle} />
              </div>
              <div style={{ flex: 1, minWidth: "80px" }}>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>نماد</label>
                <input type="text" value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="kg" dir="ltr" style={inputStyle} />
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={isCreating}>
                {isCreating ? "در حال ذخیره..." : "ذخیره"}
              </button>
              <button className="btn btn-secondary" onClick={() => { setShowForm(false); setError(null); }}>انصراف</button>
            </div>
          </div>
        )}

        {/* جدول */}
        {isLoading ? (
          <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری...</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "right" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--glass-border)", background: "rgba(255,255,255,0.04)" }}>
                <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "600" }}>کد</th>
                <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "600" }}>نام</th>
                <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "600" }}>نماد</th>
                <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "600" }}>وضعیت</th>
                {canManage && <th style={{ padding: "0.75rem 1rem" }}></th>}
              </tr>
            </thead>
            <tbody>
              {(data?.results ?? []).length === 0 ? (
                <tr><td colSpan={5} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>هیچ واحدی تعریف نشده.</td></tr>
              ) : (
                (data?.results ?? []).map((uom) => (
                  <tr key={uom.id} style={{
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                  }}>
                    <td style={{ padding: "0.85rem 1rem", fontFamily: "monospace", fontWeight: "bold" }} dir="ltr">{uom.code}</td>
                    <td style={{ padding: "0.85rem 1rem" }}>{uom.name}</td>
                    <td style={{ padding: "0.85rem 1rem", color: "var(--color-text-muted)" }} dir="ltr">{uom.symbol || "—"}</td>
                    <td style={{ padding: "0.85rem 1rem" }}>
                      <span style={{
                        padding: "0.2rem 0.6rem", borderRadius: "100px", fontSize: "0.85rem",
                        background: uom.is_active ? "rgba(0,255,0,0.1)" : "rgba(128,128,128,0.15)",
                        color: uom.is_active ? "var(--color-success)" : "var(--color-text-muted)",
                      }}>
                        {uom.is_active ? "فعال" : "غیرفعال"}
                      </span>
                    </td>
                    {canManage && (
                      <td style={{ padding: "0.85rem 1rem", textAlign: "left" }}>
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: "0.8rem", padding: "0.25rem 0.6rem" }}
                          onClick={() => deactivate({ id: uom.id, data: { is_active: !uom.is_active } })}
                        >
                          {uom.is_active ? "غیرفعال" : "فعال"}
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
